import $ from 'jquery';

import CommonData from 'app/src/data/CommonData';
import Payment from 'app/src/data/models/Payment';
import PaymentTransaction from 'app/src/data/models/PaymentTransaction';
import Localize from 'app/src/localize/Localize';
import PaymentServiceTerminal from 'app/src/payments/terminals/PaymentServiceTerminal';
import Terminal, { TerminalTransactionResponse } from 'app/src/payments/terminals/Terminal';

const _Status = {
  APPROVED: 'Approved',
  DECLINED: 'Declined',
  ERROR: 'Error',
  CONNECTION_TIMEOUT: 'Connection Timeout',
  TERMINAL_BUSY: 'Terminal Busy',
  CANCELLED: 'Cancelled',
};

const _DESCRIPTION = 'Dejavoo Payment Terminal';
const _TERMINAL_REQUEST_TIMEOUT_INTERVAL = 30000; // 30 seconds
const _STATUS_POLL_PERIOD = 5000; // 5 seconds

export default class VantivBlacklineTerminal extends PaymentServiceTerminal {
  static get Status() {
    return _Status;
  }

  /**
   * Constructor for a VantivBlacklineTerminal object.
   *
   * @param terminalModel {CCTerminal}    The API model representation of the terminal
   */
  constructor(terminalModel) {
    super(terminalModel);
  }

  /**
   * Communicates with the terminal to start a transaction.
   *
   * @param {Object} paymentOptions
   * @param {Decimal} paymentOptions.amount  The amount to transact with the terminal.
   * @param {Number} paymentOptions.saleID   The ID of the sale to which the transaction will be associated
   * @param {GatewayConfig} paymentOptions.gatewayConfig  The gateway configuration used to transact the request
   */
  processPayment(paymentOptions) {
    let deferred = $.Deferred();

    this.stagePayment(paymentOptions)
      .done(({ payment, terminalPayment, paymentTransaction }) => {
        if (payment.terminalApiUrl) {
          // initiate the transaction with the terminal using the server-supplied URL
          this._initiateTerminalRequest(payment.terminalApiUrl)
            .done((response) => {
              paymentTransaction.gatewayResponse = response;
              terminalPayment.rawResponse = paymentTransaction;

              switch (response.ResultCode) {
                case _Status.APPROVED:
                  deferred.resolve(
                    new TerminalTransactionResponse(
                      Terminal.Response.APPROVED,
                      response.ResultCode,
                      terminalPayment
                    )
                  );
                  break;

                case _Status.DECLINED:
                  deferred.reject(
                    new TerminalTransactionResponse(
                      Terminal.Response.DECLINED,
                      response.ResultCode,
                      terminalPayment,
                      response.Message
                    )
                  );
                  break;

                case _Status.ERROR:
                  deferred.reject(
                    new TerminalTransactionResponse(
                      Terminal.Response.TERMINAL_ERROR,
                      response.ResultCode,
                      terminalPayment,
                      response.Message
                    )
                  );
                  break;

                case _Status.CONNECTION_TIMEOUT:
                  deferred.reject(
                    new TerminalTransactionResponse(
                      Terminal.Response.TIMEOUT,
                      response.ResultCode,
                      terminalPayment,
                      response.Message
                    )
                  );
                  break;

                case _Status.TERMINAL_BUSY:
                  deferred.reject(
                    new TerminalTransactionResponse(
                      Terminal.Response.COULD_NOT_START_TERMINAL,
                      response.ResultCode,
                      terminalPayment,
                      response.Message
                    )
                  );
                  break;

                case _Status.CANCELLED:
                  deferred.reject(
                    new TerminalTransactionResponse(
                      Terminal.Response.CANCELLED_FROM_TERMINAL,
                      response.ResultCode,
                      terminalPayment,
                      response.Message
                    )
                  );
                  break;

                default:
                  deferred.reject(
                    new TerminalTransactionResponse(
                      Terminal.Response.TERMINAL_ERROR,
                      response.ResultCode,
                      terminalPayment,
                      response.Message
                    )
                  );
                  break;
              }
            })
            .fail((jqXHR, status) => {
              if (status !== 'timeout' && status !== 'abort') {
                paymentTransaction.gatewayResponse = jqXHR.responseText;
                terminalPayment.rawResponse = paymentTransaction;

                deferred.reject(
                  new TerminalTransactionResponse(
                    Terminal.Response.ERROR,
                    _Status.ERROR,
                    terminalPayment,
                    Terminal.Response.UNKNOWN
                  )
                );
              } else {
                // If the request timed out, try getting the result of
                // the payment from the back-end which should be polling
                // for the payment status by now.
                this.pollApiForPaymentStatus(payment)
                  .done(() => {
                    deferred
                      .resolve(
                        new TerminalTransactionResponse(
                          Terminal.Response.PAYMENT_ALREADY_RECONCILED,
                          payment.get('status'),
                          // doesn't really matter what we send here but we need
                          // a payment so that the terminal conforms to the
                          // general workflow
                          terminalPayment
                        )
                      )
                      .fail(() => {
                        deferred.reject(
                          new TerminalTransactionResponse(
                            Terminal.Response.ERROR,
                            _Status.ERROR,
                            terminalPayment,
                            Localize.translate(
                              'The status of the transaction could not be determined.'
                            )
                          )
                        );
                      });
                  })
                  .fail(() => {
                    // map the payment service status to the terminal response
                    let response;
                    switch (payment.get('status')) {
                      case Payment.Status.CANCELLED:
                        response = Terminal.Response.CANCELLED_FROM_TERMINAL;
                        break;

                      case Payment.Status.DECLINED:
                        response = Terminal.Response.DECLINED;
                        break;

                      case Payment.Status.ERROR:
                      case Payment.Status.UNKNOWN:
                        response = Terminal.Response.ERROR;
                        break;

                      default:
                        response = Terminal.Response.PAYMENT_ALREADY_RECONCILED;
                        break;
                    }

                    deferred.reject(
                      new TerminalTransactionResponse(response, payment.get('status'))
                    );
                  });
              }
            });
        } else {
          deferred.reject(
            new TerminalTransactionResponse(
              Terminal.Response.ERROR,
              _Status.ERROR,
              terminalPayment,
              Localize.translate('The transaction could not be started.')
            )
          );
        }
      })
      .fail((transactionResponse) => {
        deferred.reject(transactionResponse);
      });

    return deferred.promise();
  }

  nextPollingInterval() {
    return _STATUS_POLL_PERIOD;
  }

  /**
   * Communicates with the terminal to cancel the request.
   */
  cancelTransaction() {
    let deferred = $.Deferred();

    // cleanup
    if (this._pollingTimer) {
      clearTimeout(this._pollingTimer);
      delete this._pollingTimer;
    }

    // this terminal type does not support cancelling via the POS
    deferred.reject();

    return deferred.promise();
  }

  /**
   * Communicates with the terminal to check the status of the terminal.  Resolves with the appropriate
   * status.
   */
  checkStatus() {
    let deferred = $.Deferred();

    // There is no status check for a Blackline terminal so just assume it's online
    // and resolve the promise immediately
    deferred.resolve(Terminal.Status.ONLINE);

    return deferred.promise();
  }

  /**
   * Returns a string describing the terminal.
   *
   * @returns {string}    A description of the terminal
   */
  description() {
    return _DESCRIPTION;
  }

  /**
   * Voids a transaction using the terminal.  This is necessary because terminal interaction
   * is required with this type of terminal in order to void a transaction.
   *
   * @param {Payment} payment The payment to void
   * @param {Payment} servicePayment  The original payment to be voided
   * @returns {jQuery.Promise}
   */
  voidTransaction(payment, servicePayment) {
    let deferred = $.Deferred();

    servicePayment
      .voidPayment(this.model.id)
      .done(() => {
        let paymentTransaction = new PaymentTransaction({
          paymentTransactionID: servicePayment.nextPaymentTransactionID,
          gateway: servicePayment.paymentTransaction.get('gateway'),
          paymentID: servicePayment.id,
          terminalID: this.model.id,
          shopID: CommonData.selectedShop.id,
          registerID: CommonData.selectedRegister.id,
        });
        paymentTransaction.gatewayRequest = servicePayment.terminalApiUrl;
        this._initiateTerminalRequest(servicePayment.terminalApiUrl).always((response) => {
          paymentTransaction.gatewayResponse = response;
          payment.rawResponse = paymentTransaction;

          switch (response.ResultCode) {
            case _Status.APPROVED:
              deferred.resolve();
              break;

            default:
              deferred.reject(response.Message);
              break;
          }
        });
      })
      .fail(() => {
        deferred.reject(Localize.translate('There was an error communicating with the server.'));
      });
    return deferred.promise();
  }

  /**
   * Contacts the physical terminal to initialize payments, cancel payments,
   * check status, etc.
   *
   * @param url {String} The URL to contact
   * @private
   */
  _initiateTerminalRequest(url) {
    var ajaxOptions = {
      type: 'GET',
      url,
      timeout: _TERMINAL_REQUEST_TIMEOUT_INTERVAL,
    };

    return $.ajax(ajaxOptions);
  }
}
